import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Play, Pause, X, Volume2, VolumeX, CloudRainWind } from "lucide-react";
import { Reveal, MaskedLine, Eyebrow } from "./Reveal";
import { Watermark } from "./Logo";

const STILL_1 =
  "https://customer-assets-v7afamib.emergentagent.net/job_danimorr/artifacts/fab4270bf7dad60d_woods%20dm.jpeg";
const STILL_2 =
  "https://images.unsplash.com/photo-1674033846163-fe3ea31c1f97?crop=entropy&cs=srgb&fm=jpg&q=85";
const POSTER =
  "https://customer-assets-v7afamib.emergentagent.net/job_danimorr/artifacts/4f59752f2493767c_wtbws%20photo.jpg";
const NIGHT_ROAD =
  "https://images.unsplash.com/photo-1729882417531-eead4fe0bb2b?crop=entropy&cs=srgb&fm=jpg&q=85";
const CRT_1 =
  "https://customer-assets-v7afamib.emergentagent.net/job_danimorr/artifacts/c8026f2736247654_Dynamic_pan_around_202602242107_k0wbs.00_03_32_07.Still009.jpg";
const CRT_2 =
  "https://customer-assets-v7afamib.emergentagent.net/job_danimorr/artifacts/06b4af5d4025167a_Shake%20-%20Dani%202026.00_00_55_19.Still008.jpg";

const RoleChip = ({ children }) => (
  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold border border-gold/40 rounded-full px-4 py-1.5">
    {children}
  </span>
);

const Cinematic = () => {
  const [teaserOpen, setTeaserOpen] = useState(false);
  const [teaserMuted, setTeaserMuted] = useState(false);
  const [snippetPlaying, setSnippetPlaying] = useState(false);
  const [storySoundOn, setStorySoundOn] = useState(false);
  const snippetRef = useRef(null);
  const snippetBoxRef = useRef(null);
  const teaserRef = useRef(null);
  const stormRef = useRef(null);

  // Song snippet hushes when scrolled out of view, stays off until pressed again
  useEffect(() => {
    const v = snippetRef.current;
    const box = snippetBoxRef.current;
    if (!v || !box) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting && !v.paused) v.pause();
      },
      { threshold: 0.15 }
    );
    obs.observe(box);
    return () => obs.disconnect();
  }, []);

  const toggleStorySound = () => {
    const a = stormRef.current;
    if (!a) return;
    if (a.paused) {
      a.play().catch(() => {});
    } else {
      a.pause();
    }
  };

  const openTeaser = () => {
    setTeaserMuted(false);
    setTeaserOpen(true);
  };

  const toggleTeaserSound = (e) => {
    e.stopPropagation();
    const v = teaserRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setTeaserMuted(v.muted);
  };

  const toggleSnippet = () => {
    const v = snippetRef.current;
    if (!v) return;
    if (v.paused) {
      v.play().catch(() => {});
    } else {
      v.pause();
    }
  };

  return (
    <section id="cinematic" data-testid="cinematic-section" className="relative bg-surface py-28 lg:py-40">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <Reveal>
          <Eyebrow index="03" label="Cinematic & Visual Works" />
        </Reveal>
        <h2 className="mt-10 font-serif font-light text-cream text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-3xl">
          <MaskedLine delay={0.05}>Stories framed like</MaskedLine>
          <MaskedLine delay={0.15}>
            <em className="italic text-gold">lost television broadcasts.</em>
          </MaskedLine>
        </h2>

        <div className="mt-20 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center" data-testid="project-beast-waits">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <Reveal delay={0.1}>
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber">
                Featured Project — Independent Dark Fantasy Film
              </p>
              <h3 className="font-serif font-light text-4xl lg:text-5xl text-cream mt-5">
                Where the <em className="italic text-gold">Beast</em> Waits
              </h3>
              <div className="flex flex-wrap gap-3 mt-6">
                <RoleChip>Writer</RoleChip>
                <RoleChip>Director</RoleChip>
                <RoleChip>Producer</RoleChip>
                <RoleChip>Editor</RoleChip>
              </div>
              <div className="mt-7 max-w-xl">
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold mb-4">
                  Synopsis
                </p>
                <audio
                  ref={stormRef}
                  loop
                  preload="none"
                  onPlay={() => setStorySoundOn(true)}
                  onPause={() => setStorySoundOn(false)}
                >
                  <source src="/assets/storm-ambience.ogg" type="audio/ogg" />
                  <source src="/assets/storm-ambience.mp3" type="audio/mpeg" />
                </audio>
                <button
                  data-testid="story-sound-toggle"
                  onClick={toggleStorySound}
                  className="group inline-flex items-center gap-3 border border-gold/50 rounded-full pl-2 pr-6 py-2 hover:border-gold transition-colors duration-300"
                >
                  <span className="w-9 h-9 rounded-full border border-gold bg-ink/60 flex items-center justify-center group-hover:bg-gold transition-colors duration-300">
                    {storySoundOn ? (
                      <Pause size={13} className="text-gold fill-gold group-hover:text-ink group-hover:fill-ink" />
                    ) : (
                      <Play size={13} className="text-gold fill-gold ml-0.5 group-hover:text-ink group-hover:fill-ink" />
                    )}
                  </span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold text-left leading-relaxed">
                    Press here before reading
                    <br />
                    to set the tone — then read
                  </span>
                </button>
                <p
                  data-testid="story-sound-status"
                  className="font-mono text-[9px] uppercase tracking-[0.2em] mt-3 transition-colors duration-300"
                  style={{ color: storySoundOn ? "#C18C3D" : "rgba(230,223,211,0.35)" }}
                >
                  {storySoundOn
                    ? "● Wind & thunder — storm is rolling"
                    : "Creepy wind & thunderstorm ambience — loops"}
                </p>
                <p className="font-serif italic font-light text-base lg:text-lg text-cream/85 leading-relaxed mt-7">
                  In the quiet of a summer afternoon, a game of hide-and-seek was being
                  played by three sisters: Valeria, Gabriela, and Adera. In the blink of an
                  eye, two vanished into thin air. No tracks. No answers. Just a chilling
                  emptiness, and a home that slowly tore itself apart under the weight of
                  what was lost.
                </p>
                <p className="font-serif italic font-light text-base lg:text-lg text-gold leading-relaxed mt-4">
                  That was twenty years ago. Now, the path leads back to where it all began.
                </p>
                <p className="font-serif italic font-light text-base lg:text-lg text-cream/85 leading-relaxed mt-4">
                  The death of Grandmother Viola was supposed to be the end of the story.
                  Instead, it was an invitation. Though distant and guarded with her heavy
                  secrets, Viola was bound to Gabriela by a deep, quiet love. When she
                  passed away, she left the Lunavorne home completely vacant. Returning to
                  settle the estate, Gabriela expected only dust and memory—until the
                  house began to answer.
                </p>
                <p className="font-serif italic font-light text-base lg:text-lg text-cream/85 leading-relaxed mt-4">
                  It started as a low, rhythmic hum vibrating through the floorboards,
                  pulsing like a hidden heart beneath the house. Step by step, the sound
                  pulled her toward the shadowed corners of her past. Standing before the
                  quiet center of the room, the hum grew deafening. The air thickened, the
                  edges of the room blurred and dissolved, and the threshold
                  opened—drawing her out of the home she knew and plunging her into the
                  mysterious world on the other side.
                </p>
                <p className="text-sm text-cream/55 leading-relaxed mt-6">
                  An atmospheric exploration of dark fantasy, rich lore, and grounded character
                  dynamics — built with meticulous cinematic worldbuilding, green-screen
                  compositing, and vintage color grading.
                </p>
                <p className="font-mono text-[8px] uppercase tracking-[0.18em] text-cream/25 mt-6">
                  Ambience: "Summer thunderstorm in the woods" — Serg Childed, CC BY-SA 4.0
                </p>
              </div>
              <button
                data-testid="end-rain-button"
                onClick={() => stormRef.current?.pause()}
                className={`group mt-9 inline-flex items-center gap-3 border rounded-full pl-2 pr-6 py-2 transition-colors duration-300 ${
                  storySoundOn
                    ? "border-gold/60 hover:border-gold"
                    : "border-cream/15 hover:border-gold/50"
                }`}
              >
                <span className="w-9 h-9 rounded-full border border-current flex items-center justify-center">
                  <CloudRainWind
                    size={14}
                    className={`transition-colors duration-300 ${
                      storySoundOn ? "text-gold" : "text-cream/50 group-hover:text-gold"
                    }`}
                  />
                </span>
                <span
                  className={`font-mono text-[10px] uppercase tracking-[0.22em] text-left leading-relaxed transition-colors duration-300 ${
                    storySoundOn ? "text-gold" : "text-cream/50 group-hover:text-gold"
                  }`}
                >
                  Press here to end the rain
                  <br />
                  or listen a while longer
                </span>
              </button>
              <button
                data-testid="watch-teaser-button"
                onClick={openTeaser}
                className="group mt-4 inline-flex items-center gap-3 border border-gold/60 text-gold font-mono text-[11px] uppercase tracking-[0.25em] px-8 py-4 rounded-full hover:bg-gold hover:text-ink transition-colors duration-300"
              >
                <Play size={13} className="fill-current" />
                Watch Teaser
              </button>
            </Reveal>
          </div>
          <div className="lg:col-span-6 order-1 lg:order-2 relative">
            <Reveal delay={0.15}>
              <div
                className="group relative clip-frame border border-gold/40 overflow-hidden cursor-pointer"
                onClick={openTeaser}
              >
                <img
                  src={STILL_1}
                  alt="Film still — Where the Beast Waits"
                  className="w-full h-[320px] lg:h-[440px] object-cover analog-grade transition-transform duration-[1.4s] ease-out group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-surface/70 via-transparent to-transparent" />
              </div>
            </Reveal>
            <Reveal delay={0.28} className="hidden lg:block absolute -bottom-10 -left-12 w-52">
              <div className="clip-frame-tl border border-amber/50 overflow-hidden shadow-2xl">
                <img
                  src={STILL_2}
                  alt="Dark fantasy atmosphere still"
                  className="w-full h-36 object-cover analog-grade"
                  loading="lazy"
                />
              </div>
            </Reveal>
          </div>
        </div>

        <Reveal delay={0.12} className="mt-20 lg:mt-28">
          <div className="relative max-w-3xl mx-auto px-2" data-testid="movie-cover-spotlight">
            <div className="clip-frame border border-gold/40 overflow-hidden relative z-10">
              <img
                src={POSTER}
                alt="Where the Beast Waits — official movie cover"
                className="w-full h-auto analog-grade"
                loading="lazy"
              />
            </div>
            <div
              className="poster-glow absolute -bottom-16 inset-x-6 h-44 pointer-events-none"
              aria-hidden="true"
            />
            <p className="relative z-10 font-mono text-[10px] uppercase tracking-[0.35em] text-gold/70 mt-8 text-center">
              Official Movie Cover
            </p>
            <p
              data-testid="cover-tagline"
              className="cover-tagline relative z-10 font-serif italic font-light text-xl lg:text-2xl text-cream/90 text-center mt-6 leading-relaxed"
            >
              "Not all doors lead to familiar rooms.
              <br />
              <span className="text-gold">Some open to entire other worlds."</span>
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.1} className="mt-20 lg:mt-28">
          <div
            data-testid="why-this-matters"
            className="projection-lit relative max-w-3xl mx-auto text-center px-8 py-14 lg:py-16 border-y border-gold/20"
          >
            <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-gold">
              Why This Matters
            </p>
            <p className="projection-text font-serif font-light text-xl lg:text-2xl text-cream/90 leading-relaxed mt-7">
              Where the Beast Waits is about creating powerful opportunities and centering
              Black actors in rich, complex dark fantasy. It's about championing incredible
              talent, bringing depth and mystery to the forefront, and building a space where
              brilliant artists take the lead in a genre we love.
            </p>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold/70 mt-7 leading-loose">
              We want you involved as a community
              <br />
              to keep bringing bold, original art to life.
            </p>
          </div>
        </Reveal>

        <div className="mt-28 lg:mt-36 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center" data-testid="project-analog-broadcast">
          <div className="lg:col-span-6 relative">
            <Reveal delay={0.1}>
              <div className="scanlines crt-flicker group relative clip-frame-tl border border-moss/70 overflow-hidden">
                <img
                  src={CRT_1}
                  alt="CRT television static — The Analog Broadcast Series"
                  className="w-full h-[320px] lg:h-[420px] object-cover analog-grade transition-transform duration-[1.4s] ease-out group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-surface/80 via-transparent to-transparent z-[6]" />
                <span className="absolute top-4 left-5 z-[7] font-mono text-[10px] tracking-[0.25em] text-gold">
                  CH — 03 · REC ●
                </span>
                <span className="absolute bottom-4 left-5 z-[7] font-mono text-[10px] tracking-[0.25em] text-cream/70">
                  SIGNAL — VHS / HI-FI
                </span>
              </div>
            </Reveal>
            <Reveal delay={0.25} className="hidden lg:block absolute -bottom-10 -right-10 w-56">
              <div className="scanlines clip-frame border border-gold/40 overflow-hidden shadow-2xl">
                <img
                  src={CRT_2}
                  alt="Vintage CRT television"
                  className="w-full h-36 object-cover analog-grade"
                  loading="lazy"
                />
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-6">
            <Reveal delay={0.15}>
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber">
                Featured Visuals — Visual Album &amp; Channel Transitions
              </p>
              <h3 className="font-serif font-light text-4xl lg:text-5xl text-cream mt-5">
                The <em className="italic text-gold">Analog Broadcast</em> Series
              </h3>
              <p className="text-base text-cream/65 leading-relaxed mt-7 max-w-xl">
                A visual album concept presented through vintage CRT television channels, VHS
                glow textures, scan lines, and seamless visual transitions designed to match
                every track on the project.
              </p>
              <div className="mt-8 border-l-2 border-moss pl-6">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-cream/40 leading-loose">
                  CRT Channels / VHS Glow / Scan Lines
                  <br />
                  Seamless Track-Matched Transitions
                </p>
              </div>

              <div className="mt-10 flex items-center gap-5" data-testid="song-snippet" ref={snippetBoxRef}>
                <div
                  className="scanlines relative w-36 lg:w-40 shrink-0 clip-frame border border-gold/50 overflow-hidden cursor-pointer group"
                  onClick={toggleSnippet}
                  data-testid="song-snippet-frame"
                >
                  <video
                    ref={snippetRef}
                    className="w-full aspect-square object-cover analog-grade"
                    loop
                    playsInline
                    preload="metadata"
                    onPlay={() => setSnippetPlaying(true)}
                    onPause={() => setSnippetPlaying(false)}
                  >
                    <source src="/assets/song-snippet.webm" type="video/webm" />
                    <source src="/assets/song-snippet.mp4" type="video/mp4" />
                  </video>
                  <div
                    className={`absolute inset-0 z-[7] flex items-center justify-center bg-ink/45 transition-opacity duration-300 ${
                      snippetPlaying ? "opacity-0 group-hover:opacity-100" : "opacity-100"
                    }`}
                  >
                    <span className="w-11 h-11 rounded-full border border-gold bg-ink/60 backdrop-blur-sm flex items-center justify-center">
                      {snippetPlaying ? (
                        <Pause size={15} className="text-gold fill-gold" />
                      ) : (
                        <Play size={15} className="text-gold fill-gold ml-0.5" />
                      )}
                    </span>
                  </div>
                </div>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">
                    Song Snippet — 20 Secs
                  </p>
                  <p className="font-serif italic text-lg text-cream mt-1.5">
                    "So Good" by Dani Morr — press play, it loops.
                  </p>
                  <p
                    className="font-mono text-[9px] uppercase tracking-[0.2em] mt-2 transition-colors duration-300"
                    style={{ color: snippetPlaying ? "#C18C3D" : "rgba(230,223,211,0.35)" }}
                    data-testid="snippet-status"
                  >
                    {snippetPlaying ? "● Sound On — Playing" : "Sound On — Tap the Frame"}
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>

        <Reveal delay={0.1} className="mt-16 lg:mt-24">
          <div className="max-w-3xl mx-auto" data-testid="analog-loop-frame">
            <div className="scanlines crt-flicker relative clip-frame border border-gold/40 overflow-hidden bg-black">
              <video
                data-testid="analog-loop-video"
                className="w-full aspect-video object-cover analog-grade"
                autoPlay
                muted
                loop
                playsInline
              >
                <source src="/assets/analog-loop.webm" type="video/webm" />
                <source src="/assets/analog-loop.mp4" type="video/mp4" />
              </video>
              <span className="absolute top-3 left-4 z-[7] font-mono text-[10px] tracking-[0.25em] text-gold">
                CH — 07 · LIVE FEED
              </span>
              <span className="absolute bottom-3 right-4 z-[7] font-mono text-[10px] tracking-[0.25em] text-cream/70">
                LOOP ● 02:51
              </span>
            </div>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-cream/35 mt-4 text-center">
              Broadcast interlude — running on a loop
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.1} className="mt-28 lg:mt-36">
          <div className="relative clip-frame border border-cream/15 overflow-hidden" data-testid="night-forest-banner">
            <img
              src={NIGHT_ROAD}
              alt="Night forest road — cinematic atmosphere"
              className="w-full h-[240px] lg:h-[360px] object-cover analog-grade"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-ink/55 flex items-center justify-center">
              <p className="font-serif italic font-light text-2xl lg:text-4xl text-cream/90 text-center px-6">
                "Every frame, a self-contained realm."
              </p>
            </div>
          </div>
        </Reveal>
      </div>

      <AnimatePresence>
        {teaserOpen && (
          <motion.div
            key="teaser-lightbox"
            data-testid="teaser-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-[75] bg-ink/95 backdrop-blur-md flex items-center justify-center px-6"
            onClick={() => setTeaserOpen(false)}
          >
            <motion.div
              initial={{ opacity: 0, y: 40, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 24, scale: 0.97 }}
              transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
              className="relative w-full max-w-4xl"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                data-testid="teaser-close-button"
                onClick={() => setTeaserOpen(false)}
                aria-label="Close teaser"
                className="absolute -top-12 right-0 text-cream/60 hover:text-gold transition-colors duration-300"
              >
                <X size={26} />
              </button>
              <div className="scanlines relative clip-frame border border-gold/50 overflow-hidden bg-black">
                <video
                  ref={teaserRef}
                  data-testid="teaser-video"
                  className="w-full h-[46vh] lg:h-[58vh] object-cover analog-grade"
                  autoPlay
                  loop
                  controls
                  controlsList="nodownload noremoteplayback"
                  disablePictureInPicture
                  disableRemotePlayback
                  muted={teaserMuted}
                  playsInline
                >
                  <source src="/assets/teaser.webm" type="video/webm" />
                  <source src="/assets/teaser.mp4" type="video/mp4" />
                </video>
                <div className="absolute top-0 inset-x-0 z-[6] flex items-start justify-between gap-6 p-5 pointer-events-none bg-gradient-to-b from-ink/70 to-transparent">
                  <p className="font-serif italic font-light text-xl lg:text-2xl text-cream">
                    Where the Beast Waits
                  </p>
                  <div className="flex items-center gap-4 shrink-0 mt-1.5">
                    <p className="hidden sm:block font-mono text-[10px] uppercase tracking-[0.35em] text-gold">
                      Official Teaser
                    </p>
                    <button
                      data-testid="teaser-sound-toggle"
                      onClick={toggleTeaserSound}
                      aria-label={teaserMuted ? "Unmute teaser sound" : "Mute teaser sound"}
                      className="pointer-events-auto inline-flex items-center gap-2 border border-gold/50 bg-ink/60 backdrop-blur-sm rounded-full px-4 py-2 font-mono text-[9px] uppercase tracking-[0.25em] text-gold hover:bg-gold hover:text-ink transition-colors duration-300"
                    >
                      {teaserMuted ? <VolumeX size={13} /> : <Volume2 size={13} />}
                      {teaserMuted ? "Sound Off" : "Sound On"}
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Cinematic;
